import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { documentsAPI, assetsAPI } from '../lib/api';
import { formatDateTime } from '../lib/formatters';
import { FileText, Upload, Trash2, Download, Filter, Search, File, FileSpreadsheet, FileImage, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { value: 'registration', label: 'Registration' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'maintenance', label: 'Maintenance Record' },
  { value: 'contract', label: 'Contract' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];

const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return <FileText className="w-5 h-5 text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FileImage className="w-5 h-5 text-blue-500" />;
  return <File className="w-5 h-5 text-[hsl(var(--text-secondary))]" />;
};

export default function DocumentsPage() {
  const { isAdmin } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    asset_id: '',
    document_type: 'other',
    title: '',
    description: '',
    file_name: '',
    file_url: '',
  });

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (selectedAsset) {
      fetchDocuments(selectedAsset);
    } else {
      setDocuments([]);
    }
  }, [selectedAsset]);

  const fetchAssets = async () => {
    try {
      const response = await assetsAPI.list({});
      setAssets(response.data);
      if (response.data.length > 0) {
        setSelectedAsset(response.data[0].asset_id);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load assets');
    }
  };

  const fetchDocuments = async (assetId) => {
    try {
      setLoading(true);
      const response = await documentsAPI.getByAsset(assetId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.asset_id || !uploadForm.title || !uploadForm.file_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await documentsAPI.upload(uploadForm);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({
        asset_id: selectedAsset,
        document_type: 'other',
        title: '',
        description: '',
        file_name: '',
        file_url: '',
      });
      if (selectedAsset) {
        fetchDocuments(selectedAsset);
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentsAPI.delete(documentId);
      toast.success('Document deleted');
      if (selectedAsset) {
        fetchDocuments(selectedAsset);
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter((doc) => {
    const matchesType = !filterType || doc.document_type === filterType;
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.file_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const selectedAssetData = assets.find(a => a.asset_id === selectedAsset);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Asset Documents
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            View and manage documentation for fleet assets
          </p>
        </div>

        {/* Asset Selector */}
        <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-6 mb-6">
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            Select Asset
          </label>
          <select
            data-testid="asset-selector"
            value={selectedAsset}
            onChange={(e) => setSelectedAsset(e.target.value)}
            className="w-full md:w-96 px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white text-[hsl(var(--text-primary))] focus:ring-2 focus:ring-[hsl(var(--brand-primary))] focus:border-transparent"
          >
            <option value="">Select an asset...</option>
            {assets.map((asset) => (
              <option key={asset.asset_id} value={asset.asset_id}>
                {asset.unit_number} - {asset.name}
              </option>
            ))}
          </select>
          
          {selectedAssetData && (
            <div className="mt-4 flex items-center gap-4 text-sm text-[hsl(var(--text-secondary))]">
              <span className="px-2 py-1 bg-[hsl(var(--background-secondary))] rounded">
                {selectedAssetData.type.toUpperCase()}
              </span>
              <span>Total Shares: {selectedAssetData.total_shares}</span>
              <span>Status: {selectedAssetData.status}</span>
            </div>
          )}
        </div>

        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" />
              <input
                type="text"
                data-testid="document-search"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white text-[hsl(var(--text-primary))] placeholder:text-[hsl(var(--text-tertiary))] focus:ring-2 focus:ring-[hsl(var(--brand-primary))] focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter by Type */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-[hsl(var(--text-tertiary))]" />
            <select
              data-testid="type-filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white text-[hsl(var(--text-primary))] focus:ring-2 focus:ring-[hsl(var(--brand-primary))] focus:border-transparent"
            >
              <option value="">All Types</option>
              {DOCUMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Upload Button */}
          {selectedAsset && (
            <button
              data-testid="upload-document-btn"
              onClick={() => {
                setUploadForm({ ...uploadForm, asset_id: selectedAsset });
                setShowUploadModal(true);
              }}
              className="flex items-center gap-2 px-6 py-3 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
            >
              <Upload className="w-5 h-5" />
              Upload Document
            </button>
          )}
        </div>

        {/* Documents List */}
        {loading ? (
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <div className="animate-pulse">Loading documents...</div>
          </div>
        ) : !selectedAsset ? (
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <FolderOpen className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
            <p className="text-[hsl(var(--text-secondary))]">Select an asset to view its documents</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
            <FileText className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
            <p className="text-[hsl(var(--text-secondary))]">No documents found</p>
            <p className="text-sm text-[hsl(var(--text-tertiary))] mt-2">
              {searchQuery || filterType ? 'Try adjusting your filters' : 'Upload documents to get started'}
            </p>
          </div>
        ) : (
          <div data-testid="documents-list" className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <div className="divide-y divide-[hsl(var(--border))]">
              {filteredDocuments.map((doc) => (
                <div
                  key={doc.document_id}
                  data-testid={`document-${doc.document_id}`}
                  className="p-6 hover:bg-[hsl(var(--background-secondary))] transition-colors duration-150"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: File Icon + Details */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                        {getFileIcon(doc.file_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[hsl(var(--text-primary))] mb-1 truncate">
                          {doc.title}
                        </h3>
                        <div className="text-sm text-[hsl(var(--text-secondary))] mb-2">
                          {doc.file_name}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[hsl(var(--text-tertiary))]">
                          <span className="px-2 py-1 bg-[hsl(var(--background-secondary))] rounded font-medium">
                            {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                          </span>
                          <span>Uploaded {formatDateTime(doc.uploaded_at)}</span>
                        </div>
                        {doc.description && (
                          <p className="mt-2 text-sm text-[hsl(var(--text-secondary))]">
                            {doc.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`download-${doc.document_id}`}
                        className="p-2 rounded-lg hover:bg-[hsl(var(--brand-primary))] hover:text-white text-[hsl(var(--text-secondary))] transition-colors duration-200"
                        title="Download"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                      {isAdmin() && (
                        <button
                          data-testid={`delete-${doc.document_id}`}
                          onClick={() => handleDelete(doc.document_id)}
                          className="p-2 rounded-lg hover:bg-red-500 hover:text-white text-[hsl(var(--text-secondary))] transition-colors duration-200"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[hsl(var(--border))]">
              <h2 className="text-xl font-semibold text-[hsl(var(--text-primary))]">
                Upload Document
              </h2>
            </div>

            <form onSubmit={handleUpload} className="p-6 space-y-4">
              {/* Asset Selector */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  Asset *
                </label>
                <select
                  value={uploadForm.asset_id}
                  onChange={(e) => setUploadForm({ ...uploadForm, asset_id: e.target.value })}
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white"
                  required
                >
                  <option value="">Select asset...</option>
                  {assets.map((asset) => (
                    <option key={asset.asset_id} value={asset.asset_id}>
                      {asset.unit_number} - {asset.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  Document Type *
                </label>
                <select
                  value={uploadForm.document_type}
                  onChange={(e) => setUploadForm({ ...uploadForm, document_type: e.target.value })}
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg bg-white"
                  required
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="e.g., Q1 2025 Insurance Certificate"
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  Description
                </label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Optional description..."
                  rows={3}
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg resize-none"
                />
              </div>

              {/* File Name */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  File Name *
                </label>
                <input
                  type="text"
                  value={uploadForm.file_name}
                  onChange={(e) => setUploadForm({ ...uploadForm, file_name: e.target.value })}
                  placeholder="e.g., insurance_cert_2025.pdf"
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg"
                  required
                />
              </div>

              {/* File URL */}
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-1">
                  File URL *
                </label>
                <input
                  type="url"
                  value={uploadForm.file_url}
                  onChange={(e) => setUploadForm({ ...uploadForm, file_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg"
                  required
                />
                <p className="mt-1 text-xs text-[hsl(var(--text-tertiary))]">
                  Enter the URL where the document is hosted (Google Drive, Dropbox, etc.)
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="px-6 py-3 border border-[hsl(var(--border))] rounded-lg font-medium text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--background-secondary))] transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-upload"
                  className="px-6 py-3 bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Upload Document
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
